import { Suspense, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { CheckCircle, ErrorOutline, Schedule } from '@mui/icons-material';
import { DateFormats, practiceStateSetupRoute, ROUTING_CONFIG } from '@uplift/constants';
import { canResolveRejectionWithStateExhibit, collaborativeAgreementRejectionReasonText, licenseAllowsStateExhibit, licenseRequiresCollaborativeAgreement } from '@uplift/pages';
import { ContextualNavigationLayout, Loader, useAlert } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';
import { useCollaborativeStateDetailsQuery, useProviderCollaborativeMatchRequestsQuery, useProviderExhibitSignaturesQuery, useProviderLicensesActiveCollaborativeAgreementsForLicenseQuery, useProviderLicensesStatusQuery, useRequestCollaborativePhysicianMatchMutation, useStateExhibitTemplateQuery, useStateQuery } from '@uplift/queries';
import { License } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useGoLiveLocked } from '../../hooks';
import { QuestionStep } from './components/QuestionStep';
import { CollabAgreementStep } from './components/CollabAgreementStep';
import { ExhibitSignatureStep } from './components/ExhibitSignatureStep';
import { FinalStep } from './components/FinalStep';

type WizardStep =
  | 'agreementRejectionChoice'
  | 'stateExhibitQuestion'
  | 'collaborationQuestion'
  | 'exhibitSign'
  | 'exhibitSigned'
  | 'collaborationUpload'
  | 'collaborationSubmitted'
  | 'needMatch';

type WizardProps = {
  license: License;
  matchRequested: boolean;
};

const getStepSequence = (requiresStateExhibitAndCollaborativeAgreement: boolean, showStateExhibitOption: boolean): WizardStep[] => {
  if (requiresStateExhibitAndCollaborativeAgreement) {
    return [ 'exhibitSign', 'collaborationQuestion' ];
  }

  if (showStateExhibitOption) {
    return [ 'stateExhibitQuestion', 'collaborationQuestion' ];
  }

  return [ 'collaborationQuestion' ];
};

const PracticeStateSetupWizard = ({ license, matchRequested }: WizardProps) => {
  const history = useHistory();
  const alert = useAlert();
  const requestMatch = useRequestCollaborativePhysicianMatchMutation(license.userId, license.id);

  const { data: state } = useStateQuery(license.stateId, { suspense: true });
  const { data: collaborativeStateDetails } = useCollaborativeStateDetailsQuery(license.stateId);
  const { data: exhibitTemplate } = useStateExhibitTemplateQuery(license.userId, license.id);
  const { data: exhibitSignatures } = useProviderExhibitSignaturesQuery(license.userId);
  const { data: activeCollabAgreement } = useProviderLicensesActiveCollaborativeAgreementsForLicenseQuery(license.userId, license.id);
  const requiresCollaborativeAgreement = licenseRequiresCollaborativeAgreement(license, collaborativeStateDetails);
  const allowsStateExhibit = licenseAllowsStateExhibit(license, collaborativeStateDetails);
  const showStateExhibitOption = allowsStateExhibit && !!exhibitTemplate;
  const requiresStateExhibitAndCollaborativeAgreement = requiresCollaborativeAgreement && allowsStateExhibit && collaborativeStateDetails?.authorizationRequirement === 'both';
  const hasSignedExhibit = exhibitSignatures?.some(signature =>
    signature.licenseId === license.id
    && !signature.archived
    && !CalendarDateService.date(signature.expireAt, 'YYYY-MM-DD').isBefore(CalendarDateService.date(), 'day')
  ) ?? false;
  const isRejectedResolution = activeCollabAgreement?.rejectedAt != null
    && canResolveRejectionWithStateExhibit({
      allowsStateExhibit,
      authorizationRequirement: collaborativeStateDetails?.authorizationRequirement,
      hasActiveExhibitTemplate: !!exhibitTemplate,
    });

  const stepSequence = getStepSequence(requiresStateExhibitAndCollaborativeAgreement, showStateExhibitOption);

  const [ step, setStep ] = useState<WizardStep>(() => {
    if (isRejectedResolution) {
      return 'agreementRejectionChoice';
    }

    if (requiresStateExhibitAndCollaborativeAgreement) {
      if (!hasSignedExhibit) {
        return 'exhibitSign';
      }

      return matchRequested ? 'needMatch' : 'collaborationQuestion';
    }

    if (matchRequested) {
      return 'needMatch';
    }

    if (!showStateExhibitOption) {
      return 'collaborationQuestion';
    }

    return requiresCollaborativeAgreement ? 'stateExhibitQuestion' : 'exhibitSign';
  });
  const [ stateExhibitAnswer, setStateExhibitAnswer ] = useState<string | null>(null);
  const [ collaborationAnswer, setCollaborationAnswer ] = useState<string | null>(null);
  const [ agreementRejectionAnswer, setAgreementRejectionAnswer ] = useState<string | null>(null);
  const [ exhibitExpiresAt, setExhibitExpiresAt ] = useState<string | null>(null);

  const goToPracticeStates = () => history.push(ROUTING_CONFIG.practiceStates);

  const backFromCollaborationQuestion = () => {
    if (!requiresStateExhibitAndCollaborativeAgreement && showStateExhibitOption) {
      setStep('stateExhibitQuestion');
      return;
    }

    goToPracticeStates();
  };

  const backFromExhibitSign = () => {
    if (isRejectedResolution) {
      setStep('agreementRejectionChoice');
      return;
    }

    if (!requiresStateExhibitAndCollaborativeAgreement && requiresCollaborativeAgreement) {
      setStep('stateExhibitQuestion');
      return;
    }

    goToPracticeStates();
  };

  const stepIndex = stepSequence.indexOf(step);
  const stepWord = requiresStateExhibitAndCollaborativeAgreement ? 'STEP' : 'QUESTION';
  const stepLabel = stepIndex >= 0 && stepSequence.length > 1 ? `${stepWord} ${stepIndex + 1} OF ${stepSequence.length}` : undefined;

  switch (step) {
    case 'agreementRejectionChoice':
      return (
        <QuestionStep
          banner={activeCollabAgreement && (
            <Box sx={{ bgcolor: 'error.background', border: 1, borderColor: 'error.border', borderRadius: 1, p: 2, mb: 4 }}>
              <Box display="flex" gap={1}>
                <ErrorOutline color="error" fontSize="small" sx={{ mt: '2px' }} />
                <Box>
                  <Typography variant="subtitle2" color="error.main" fontWeight={700}>
                    Your collaborative agreement was rejected
                  </Typography>
                  <Typography variant="body2" color="textPrimary" fontWeight={700} mt={0.5}>
                    {collaborativeAgreementRejectionReasonText(activeCollabAgreement.rejectionReason, activeCollabAgreement.rejectionDetails)}
                  </Typography>
                  {activeCollabAgreement.rejectedAt && (
                    <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                      Rejected {CalendarDateService.format(activeCollabAgreement.rejectedAt, DateFormats['MMMM D, YYYY'])}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          title={`How do you want to practice in ${state?.fullName}?`}
          subtitle="Pick the path that matches your license today. You can switch paths after a rejection."
          options={[
            { value: 'upload', title: 'Upload a new collaborative agreement', description: 'Replace the rejected agreement with a corrected one. We review it again.' },
            { value: 'exhibit', title: 'I qualify for independent practice', description: `Sign the ${state?.fullName} state exhibit and go live. No physician or review needed.` },
          ]}
          value={agreementRejectionAnswer}
          onChange={setAgreementRejectionAnswer}
          onContinue={() => setStep(agreementRejectionAnswer === 'upload' ? 'collaborationUpload' : 'exhibitSign')}
          onBack={goToPracticeStates}
        />
      );

    case 'stateExhibitQuestion':
      return (
        <QuestionStep
          overline={stepLabel}
          title={`Do you meet ${state?.fullName}'s requirements to practice independently?`}
          subtitle={`For example: an active ${state?.shortName} APN license and the required supervised practice hours.`}
          options={[
            { value: 'independent', title: 'Yes — I qualify for independent practice', description: 'No collaborating physician needed.' },
            { value: 'collaborative', title: "No — I'll work with a collaborating physician", description: `${state?.fullName} requires a collaborative agreement.` },
          ]}
          value={stateExhibitAnswer}
          onChange={setStateExhibitAnswer}
          onContinue={() => setStep(stateExhibitAnswer === 'independent' ? 'exhibitSign' : 'collaborationQuestion')}
          onBack={goToPracticeStates}
        />
      );

    case 'collaborationQuestion':
      return (
        <QuestionStep
          overline={stepLabel}
          title="Do you already have a collaborative agreement?"
          subtitle="We'll either use the agreement you've signed, or pair you with a supervising physician."
          options={[
            { value: 'upload', title: "Yes — I'll upload it", description: "Upload an agreement you've already signed." },
            { value: 'match', title: 'No — match me with a physician', description: 'BetterHelp pairs you with a supervising physician.' },
          ]}
          value={collaborationAnswer}
          onChange={setCollaborationAnswer}
          onContinue={async () => {
            if (collaborationAnswer === 'upload') {
              setStep('collaborationUpload');
              return;
            }

            try {
              await requestMatch.mutateAsync();
              setStep('needMatch');
            } catch (err) {
              alert.error(err.message);
            }
          }}
          onBack={backFromCollaborationQuestion}
        />
      );

    case 'collaborationUpload': {
      let onChangePracticeType: (() => void) | undefined;

      if (isRejectedResolution) {
        onChangePracticeType = () => setStep('agreementRejectionChoice');
      } else if (!requiresStateExhibitAndCollaborativeAgreement && showStateExhibitOption) {
        onChangePracticeType = () => setStep('stateExhibitQuestion');
      }

      return (
        <CollabAgreementStep
          license={license}
          stateName={state?.fullName ?? ''}
          onChangePracticeType={onChangePracticeType}
          onBack={() => setStep(isRejectedResolution ? 'agreementRejectionChoice' : 'collaborationQuestion')}
          onComplete={() => setStep('collaborationSubmitted')}
        />
      );
    }

    case 'collaborationSubmitted':
      return (
        <FinalStep
          icon={<CheckCircle color="success" />}
          title="Submitted for review"
          description={`We're reviewing your ${state?.fullName ?? 'your state'} collaborative agreement. We'll email you when you're cleared to see patients — usually within 2 business days.`}
          actionLabel="Back to Practice States"
          onAction={goToPracticeStates}
        />
      );

    case 'needMatch':
      return (
        <FinalStep
          icon={<Schedule color="primary" />}
          title="We'll take it from here"
          description="BetterHelp will pair you with a supervising physician. You can head back to Practice States — we'll let you know once you're matched."
          actionLabel="Back to Practice States"
          onAction={goToPracticeStates}
        />
      );

    case 'exhibitSign':
      return exhibitTemplate ? (
        <ExhibitSignatureStep
          providerId={license.userId}
          license={license}
          state={state}
          exhibitTemplate={exhibitTemplate}
          requiresCollaborativeAgreementNext={requiresStateExhibitAndCollaborativeAgreement}
          stepLabel={stepLabel}
          onBack={backFromExhibitSign}
          onComplete={expireAt => {
            if (requiresStateExhibitAndCollaborativeAgreement) {
              setStep(matchRequested ? 'needMatch' : 'collaborationQuestion');
              return;
            }

            setExhibitExpiresAt(expireAt);
            setStep('exhibitSigned');
          }}
        />
      ) : null;

    case 'exhibitSigned':
      return (
        <FinalStep
          icon={<CheckCircle color="success" />}
          title={`You're live in ${state?.fullName ?? 'your state'}`}
          description={`You can now see patients in ${state?.fullName ?? 'your state'}. Your signed state exhibit is on file${
            exhibitExpiresAt ? ` and expires ${CalendarDateService.format(exhibitExpiresAt, DateFormats['MMMM D, YYYY'])}` : ''
          } — we'll remind you to renew.`}
          actionLabel="Back to Practice States"
          onAction={goToPracticeStates}
        />
      );

    default:
      return null;
  }
};

const PracticeStateSetupContent = ({ stateId }: { stateId: string }) => {
  const me = useCurrentuser();
  const { data: licenses } = useProviderLicensesStatusQuery(me.id, { suspense: true });
  const { data: matchRequestsByLicenseId } = useProviderCollaborativeMatchRequestsQuery(me.id);
  const license = licenses?.find(l => l.stateId === stateId);
  const { locked: goLiveLocked, isLoading } = useGoLiveLocked(license?.providerGoLiveAllowed);
  const matchRequested = license ? matchRequestsByLicenseId?.[license.id]?.collaborativePhysicianMatchRequested ?? false : false;

  if (isLoading) {
    return <Loader py={8} />;
  }

  if (!license || goLiveLocked) {
    return <Redirect to={ROUTING_CONFIG.practiceStates} />;
  }

  return <PracticeStateSetupWizard license={license} matchRequested={matchRequested} />;
};

export const PracticeStateSetupPage = () => {
  const { stateId } = practiceStateSetupRoute.useParams();
  const history = useHistory();
  const { data: state } = useStateQuery(stateId);

  return (
    <ContextualNavigationLayout
      headerProps={{
        leftButtonText: 'Back to Practice States',
        onLeftButtonClick: () => history.push(ROUTING_CONFIG.practiceStates),
        centerBlock: <Typography variant="h6" fontWeight={700} textAlign="center">Go live in {state?.fullName ?? ''}</Typography>,
      }}
    >
      <Suspense fallback={<Loader py={8} />}>
        <PracticeStateSetupContent stateId={stateId} />
      </Suspense>
    </ContextualNavigationLayout>
  );
};
